import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import Header from '../components/Header'
import { CATEGORIES, formatDate } from '../lib/constants'
import { AREAS, areaFor, inArea } from '../lib/areas'
import { wantedFirstName } from '../lib/wanted'
import { fetchWantedBoard } from '../lib/wantedBoard'
import { startDirectMessage } from '../lib/directMessage'
import { track } from '../lib/analytics'

const daysAgo = (iso) => {
const d = Math.floor((Date.now() - new Date(iso).getTime()) / 864e5)
return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`
}
const datesLabel = (r) => r.dates_flexible || !r.date_from ? 'Flexible dates'
: r.date_to && r.date_to !== r.date_from ? `${formatDate(r.date_from)} – ${formatDate(r.date_to)}` : formatDate(r.date_from)

// /wanted and /wanted/<area> — open Wanted requests, newest first.
// Shows first name + suburb only. Not in the main menu yet; linked from
// searches with no results and area short links.
export default function Wanted() {
const { area } = useParams()
const [searchParams] = useSearchParams()
const navigate = useNavigate()
const { user } = useAuth()
const areaInfo = areaFor(area)
const [requests, setRequests] = useState(null)
const [mine, setMine] = useState([])
const [reportFor, setReportFor] = useState(null)
const [reportReason, setReportReason] = useState('')
const [messaging, setMessaging] = useState(null)
const justPosted = searchParams.get('posted')
const focusId = searchParams.get('r') || justPosted  // from the home page strip

const load = async () => {
// First names only come back from the database — full names never reach the browser
let data = []
try { data = await fetchWantedBoard() } catch (e) { data = [] }
const list = data.filter(r => !area || inArea(area, r.postcode))
setRequests(list)
if (user) {
const { data: own } = await supabase.from('wanted_requests').select('*').eq('user_id', user.id).neq('status', 'removed').order('created_at', { ascending: false })
setMine(own || [])
}
}

// eslint-disable-next-line
useEffect(() => { load() }, [area, user?.id])

// Scroll to the request they tapped on the home page
useEffect(() => {
if (!focusId || !requests) return
const el = document.getElementById(`wanted-${focusId}`)
if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
}, [focusId, requests])

const signInFirst = (why) => navigate(`/auth?next=${encodeURIComponent(window.location.pathname)}&reason=${why}`)

const markFound = async (r) => {
if (!window.confirm(`Mark "${r.title}" as found? It will be taken off the Wanted board.`)) return
const { error } = await supabase.from('wanted_requests').update({ status: 'found' }).eq('id', r.id)
if (error) { alert('Could not update: ' + error.message); return }
track('wanted_found')
load()
}

const message = async (r) => {
if (!user) return signInFirst('message')
setMessaging(r.id)
try {
const conversationId = await startDirectMessage(user.id, r.user_id)
track('wanted_message', { category: CATEGORIES.find(c => c.id === r.category_id)?.slug })
navigate(`/messages?booking=${conversationId}`)
} catch (err) {
alert('Could not start conversation: ' + err.message)
}
setMessaging(null)
}

const submitReport = async () => {
if (!reportReason.trim()) return
const { error } = await supabase.from('reports').insert({ reporter_id: user.id, reported_id: reportFor.user_id, reason: reportReason.trim(), wanted_request_id: reportFor.id })
if (error) { alert('Could not send report: ' + error.message); return }
setReportFor(null); setReportReason('')
alert('Thanks — our team will take a look.')
}

const postUrl = '/wanted/new'
const heading = areaInfo ? `Wanted in ${areaInfo.name}` : 'Wanted'

return (
<div>
<Header />
<div className="container" style={{ maxWidth: 820, padding: '1.5rem 1rem' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
<div>
<h1 style={{ fontSize: 26, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>🔎 {heading}</h1>
<p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>Things people {areaInfo ? `in ${areaInfo.name}` : 'nearby'} are looking to hire. Got one? Send them a message.</p>
</div>
<button className="btn btn-gradient" onClick={() => user ? navigate(postUrl) : signInFirst('wanted')}>+ Post a request</button>
</div>

<div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, fontSize: 13 }}>
<label htmlFor="wanted-area" style={{ color: 'var(--text-muted)' }}>Area:</label>
<select id="wanted-area" className="form-control" style={{ maxWidth: 240 }} value={areaInfo ? area.toLowerCase() : ''} onChange={e => navigate(e.target.value ? `/wanted/${e.target.value}` : '/wanted')}>
<option value="">All of Australia</option>
{Object.entries(AREAS).sort((a, b) => a[1].name.localeCompare(b[1].name)).map(([slug, a]) => <option key={slug} value={slug}>{a.name}</option>)}
</select>
</div>

{justPosted && <div className="alert alert-success" style={{ marginBottom: 16 }}>✅ Your request is live. We'll keep it up for 30 days — tap "Found it" below when you're sorted.</div>}

{mine.length > 0 && (
<div className="card card-body" style={{ marginBottom: 20 }}>
<div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>My requests</div>
{mine.map(r => {
const expired = r.status === 'open' && new Date(r.expires_at) <= new Date()
return (
<div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
<div style={{ fontSize: 14 }}><strong>{r.title}</strong> <span style={{ color: 'var(--text-muted)' }}>· {r.suburb}</span></div>
<div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
{r.status === 'found' ? <span className="badge badge-green">Found</span>
: expired ? <span className="badge badge-gray">Expired</span>
: <>
<span style={{ color: 'var(--text-muted)' }}>Closes {formatDate(r.expires_at)}</span>
<button className="btn btn-primary btn-sm" onClick={() => markFound(r)}>Found it</button>
</>}
</div>
</div>
)
})}
</div>
)}

{requests === null ? (
<div className="spinner-wrap"><div className="spinner" /></div>
) : requests.length === 0 ? (
<div className="card card-body" style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
<div style={{ fontSize: 44, marginBottom: 8 }}>📣</div>
<h3 style={{ color: 'var(--navy)', fontWeight: 700, marginBottom: 6 }}>Be the first to post what you're looking for{areaInfo ? ` in ${areaInfo.name}` : ''}</h3>
<p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>Can't find it on HireIt? Post a request and let locals know.</p>
<button className="btn btn-gradient btn-lg" onClick={() => user ? navigate(postUrl) : signInFirst('wanted')}>Post a request</button>
</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
{requests.map(r => {
const cat = CATEGORIES.find(c => c.id === r.category_id)
const name = wantedFirstName(r.first_name)
const own = user?.id === r.user_id
return (
<div key={r.id} id={`wanted-${r.id}`} className="card card-body" style={{ display: 'flex', gap: 14, alignItems: 'flex-start', ...(focusId === r.id ? { outline: '2px solid var(--primary)', outlineOffset: 2 } : {}) }}>
<div aria-hidden="true" style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, var(--primary-light) 0%, var(--accent-light) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>{cat?.icon || '📦'}</div>
<div style={{ flex: 1, minWidth: 0 }}>
<div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 16 }}>{r.title}</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)', margin: '2px 0 6px' }}>
<strong style={{ color: 'var(--text)' }}>{name}</strong> in {r.suburb} · {datesLabel(r)}{r.budget != null ? ` · Budget $${Number(r.budget).toLocaleString('en-AU')}${r.budget_period !== 'total' ? ` ${r.budget_period}` : ''}` : ''}
</div>
{r.note && <p style={{ fontSize: 14, color: 'var(--text)', margin: '0 0 8px', whiteSpace: 'pre-wrap' }}>{r.note}</p>}
<div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
<span style={{ color: 'var(--text-faint)' }}>{cat?.name} · posted {daysAgo(r.created_at)}</span>
<span style={{ flex: 1 }} />
{!own && <button className="btn btn-primary btn-sm" onClick={() => message(r)} disabled={messaging === r.id}>{messaging === r.id ? '…' : `Message ${name}`}</button>}
{!own && <button className="btn btn-ghost btn-sm" onClick={() => user ? (setReportFor(r), setReportReason('')) : signInFirst('report')} style={{ fontSize: 11 }}>⚠️ Report</button>}
{own && <span className="badge badge-primary">Your request</span>}
</div>
</div>
</div>
)
})}
</div>
)}
</div>

{reportFor && (
<div className="modal-overlay" onClick={() => setReportFor(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Report this request</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 12 }}>"{reportFor.title}" — tell us what's wrong.</p>
<textarea className="form-control" rows={3} maxLength={500} value={reportReason} onChange={e => setReportReason(e.target.value)} placeholder="e.g. spam, not allowed, offensive…" />
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
<button className="btn btn-ghost" onClick={() => setReportFor(null)}>Cancel</button>
<button className="btn btn-danger" onClick={submitReport} disabled={!reportReason.trim()}>Send report</button>
</div>
</div>
</div>
)}
</div>
)
}

